import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useApiGet } from '../../utils/httpClientTs';
import { getScheduledTasks } from '../../endpoints/scheduledTasks';
import { ScheduledTask } from '../../types/scheduledTasks';
import AlertItem from '../../Components/AlertItem';
import dayjs from 'dayjs';

const ScheduledTasks: React.FC = () => {
  const [alertState, setAlertState] = useState<{
    severity: 'success' | 'error' | 'warning' | 'info';
    description: string;
    title?: string;
  } | null>(null);

  const {
    data: tasks,
    isLoading,
    error,
  } = useApiGet<ScheduledTask[]>(['scheduled-tasks'], getScheduledTasks, {
    enabled: true,
    retry: 1,
  });

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';
    try {
      return dayjs(dateString).format('MMM DD, YYYY hh:mm A');
    } catch {
      return 'Invalid date';
    }
  };

  const formatNextRun = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Not scheduled';
    try {
      return dayjs(dateString).format('MMM DD, YYYY hh:mm A');
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={2}>
        <Typography variant="h4" color="primary">
          Scheduled Tasks
        </Typography>
      </Grid>

      {alertState && (
        <Grid item xs={12}>
          <AlertItem
            severity={alertState.severity}
            description={alertState.description}
            title={alertState.title}
            onClose={() => setAlertState(null)}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box sx={{ mb: 3 }}>
              <AlertItem
                severity="error"
                description="Failed to load scheduled tasks. Please try refreshing the page."
                onClose={() => {}}
                keepOpen={true}
              />
            </Box>
          )}

          {!isLoading && !error && tasks && (
            <>
              {tasks.length === 0 ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ py: 4 }}
                >
                  No scheduled tasks found.
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Name
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Description
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Cron Expression
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Status
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Last Run At
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Next Run At
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tasks.map((task) => (
                        <TableRow key={task.id} hover>
                          <TableCell>
                            <Typography variant="body2">{task.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {task.description || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {task.cron_expression}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                task.is_active === 1 ? 'Active' : 'Inactive'
                              }
                              color={
                                task.is_active === 1 ? 'success' : 'default'
                              }
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDateTime(task.last_run_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatNextRun(task.next_run_at)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ScheduledTasks;
